import { useMemo } from "react";
import type { ErpOrder, ErpInvoice } from "@/types/admin";

interface RecentErpActivityProps {
  orders: ErpOrder[];
  invoices: ErpInvoice[];
  loading?: boolean;
}

const ORDER_STATUS: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-secondary-100 text-secondary-800",
  processing: "bg-primary-100 text-primary-700",
  fulfilled: "bg-emerald-100 text-emerald-800",
  refunded: "bg-rose-100 text-rose-700",
  cancelled: "bg-background-200 text-foreground-600",
};

const INVOICE_STATUS: Record<string, string> = {
  draft: "bg-background-200 text-foreground-600",
  sent: "bg-secondary-100 text-secondary-800",
  paid: "bg-emerald-100 text-emerald-800",
  overdue: "bg-rose-100 text-rose-700",
  void: "bg-background-200 text-foreground-600",
};

const inr = (n: number) => `₹${n.toLocaleString("en-IN")}`;
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

interface FeedItem {
  key: string;
  kind: "order" | "invoice";
  ref: string;
  customer: string;
  plan: string;
  amount: number;
  status: string;
  date: string;
}

/**
 * Live "Recent Orders & Invoices" feed for the admin dashboard — merges the two
 * ERP streams into one newest-first activity list so new business shows up
 * without opening the individual sections.
 */
export default function RecentErpActivity({ orders, invoices, loading = false }: RecentErpActivityProps) {
  const items = useMemo<FeedItem[]>(() => {
    const merged: FeedItem[] = [
      ...orders.map((o) => ({
        key: `order-${o.id}`,
        kind: "order" as const,
        ref: o.orderNo,
        customer: o.customer,
        plan: o.plan || "Listing plan",
        amount: o.amount,
        status: o.status,
        date: o.createdAt,
      })),
      ...invoices.map((i) => ({
        key: `invoice-${i.id}`,
        kind: "invoice" as const,
        ref: i.invoiceNo,
        customer: i.customer,
        plan: i.plan || "Listing plan",
        amount: i.total,
        status: i.status,
        date: i.issuedAt,
      })),
    ];
    return merged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 7);
  }, [orders, invoices]);

  const orderCount = orders.length;
  const invoicedTotal = invoices.reduce((s, i) => s + i.total, 0);

  return (
    <div className="rounded-xl border border-background-200 bg-background-50">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-6 py-4">
        <div>
          <h3 className="font-heading text-sm font-bold text-foreground-950">Recent Orders &amp; Invoices</h3>
          <p className="mt-0.5 text-xs text-foreground-500">Latest ERP activity across all customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-[11px] font-semibold text-secondary-800">
            <i className="ri-shopping-bag-3-line text-sm" /> {loading ? "—" : `${orderCount} orders`}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-[11px] font-semibold text-accent-800">
            <i className="ri-file-list-3-line text-sm" /> {loading ? "—" : inr(invoicedTotal)}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-14 text-center">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="px-6 py-14 text-center">
          <i className="ri-inbox-line text-3xl text-foreground-300" />
          <p className="mt-2 text-sm text-foreground-500">No orders or invoices yet.</p>
          <p className="text-xs text-foreground-400">Create one from the Orders or Invoices section to see it here.</p>
        </div>
      ) : (
        <div className="divide-y divide-background-100">
          {items.map((it) => {
            const isOrder = it.kind === "order";
            const badge = isOrder
              ? ORDER_STATUS[it.status] ?? "bg-background-200 text-foreground-600"
              : INVOICE_STATUS[it.status] ?? "bg-background-200 text-foreground-600";
            return (
              <div key={it.key} className="flex items-center gap-3 px-6 py-3.5">
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    isOrder ? "bg-secondary-100 text-secondary-700" : "bg-accent-100 text-accent-800"
                  }`}
                >
                  <i className={`${isOrder ? "ri-shopping-bag-3-line" : "ri-file-list-3-line"} text-base`} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-foreground-900">
                    {it.ref} · {it.customer}
                  </p>
                  <p className="truncate text-[11px] text-foreground-500">
                    {isOrder ? "Order" : "Invoice"} · {it.plan} · {fmtDate(it.date)}
                  </p>
                </div>
                <span className={`hidden shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold capitalize sm:inline-flex ${badge}`}>
                  {it.status}
                </span>
                <span className="w-20 shrink-0 text-right text-sm font-bold tabular-nums text-foreground-950">
                  {it.amount > 0 ? inr(it.amount) : "Free"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}